import React, { useState } from 'react';
import Alert from 'components/styleguide/alert/Alert';
import InputField from 'components/styleguide/input_field/InputField';
import Button from '../../../lj_shared/button/button';
import { saveOverrides } from './utils/curationApi';
import { DirectOverrides } from './utils/curationScoring';
import { useCurationReview } from './state/CurationReviewContext';

const styles = require('./OverrideBox.module.scss');

export interface OverrideField {
  key: keyof DirectOverrides;
  label: string;
}

export interface Props {
  description: React.ReactNode;
  fields: OverrideField[];
  footnote?: string;
  title: string;
}

// Gray override box under a score card: numeric inputs applied together with
// one explicit action, like the prototype's "Apply & recalculate". A value
// supersedes the computed score until reset.
export default function OverrideBox({ description, fields, footnote, title }: Props) {
  const { dispatch, state, urls } = useCurationReview();
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [saveError, setSaveError] = useState(false);

  const valueFor = (field: OverrideField): string => {
    const draft = drafts[field.key];
    if (draft !== undefined) return draft;
    const override = state.directOverrides[field.key];
    return override !== null ? String(override) : '';
  };

  const apply = async () => {
    if (state.submitted) return;
    const changes: Partial<Record<keyof DirectOverrides, number | null>> = {};
    fields.forEach((field) => {
      const draft = drafts[field.key];
      if (draft === undefined) return;
      if (draft === '') {
        changes[field.key] = null;
        return;
      }
      const parsed = Number(draft);
      if (!Number.isNaN(parsed)) changes[field.key] = Math.min(100, Math.max(0, parsed));
    });
    if (Object.keys(changes).length === 0) return;

    setSaveError(false);
    try {
      // Persist first: a failed save keeps the typed drafts on screen instead
      // of showing them as applied.
      await saveOverrides(urls, { direct_overrides: changes });
    } catch {
      setSaveError(true);
      return;
    }

    Object.entries(changes).forEach(([key, value]) =>
      dispatch({ key: key as keyof DirectOverrides, type: 'SET_DIRECT_OVERRIDE', value: value as number | null }),
    );
    setDrafts({});
  };

  return (
    <div className={styles['ui-override-box']}>
      <span className={styles['ui-override-box__title']}>{title}</span>
      <p className={styles['ui-override-box__description']}>{description}</p>
      {saveError && <Alert message="The override couldn't be saved. Try again." variant="error" />}
      <div className={styles['ui-override-box__fields']}>
        {fields.map((field) => (
          <div className={styles['ui-override-box__field']} key={field.key}>
            <InputField
              clearable={false}
              disabled={state.submitted}
              inputMode="numeric"
              label={field.label}
              max={100}
              min={0}
              onChange={(event) => {
                // React 16 pools synthetic events: read the value before the
                // async state updater runs, or event.target is already null.
                const { value } = event.target;
                setDrafts((current) => ({ ...current, [field.key]: value }));
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') apply();
              }}
              size="S"
              type="number"
              value={valueFor(field)}
            />
          </div>
        ))}
        <Button
          buttonSize="xSmall"
          buttonType="border"
          disabled={state.submitted}
          isButton
          onClick={apply}
          type="button"
        >
          Apply &amp; recalculate
        </Button>
      </div>
      <p className={styles['ui-override-box__footnote']}>
        Clear the field and apply to return to the AI score.
      </p>
      {footnote && <p className={styles['ui-override-box__footnote']}>{footnote}</p>}
    </div>
  );
}
